import React, { useState, useEffect } from 'react';
import useAuthStore from '../stores/authStore';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';

// Import Optimize components
import OptimizeFilters from '../components/optimize/OptimizeFilters';
import PricingTable from '../components/optimize/PricingTable';
import OptimizeSummary from '../components/optimize/OptimizeSummary';
import optimizeApi from '../services/optimizeApi';

const Optimize = () => {
  const { user } = useAuthStore();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [selectedRatePlan, setSelectedRatePlan] = useState('');
  const [days, setDays] = useState(90);
  const [recommendations, setRecommendations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Load initial data (room types)
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load rate plans when room type changes
  useEffect(() => {
    if (selectedRoomType) {
      loadRatePlans();
    }
  }, [selectedRoomType]);

  // Load recommendations when filters change
  useEffect(() => {
    if (selectedRoomType) {
      loadRecommendations();
    }
  }, [selectedRoomType, selectedRatePlan, days]);

  /**
   * Load initial data (room types)
   */
  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const roomTypesData = await optimizeApi.getRoomTypes();
      setRoomTypes(roomTypesData.room_types || []);

      // Auto-select first room type
      if (roomTypesData.room_types && roomTypesData.room_types.length > 0) {
        setSelectedRoomType(roomTypesData.room_types[0].id);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load room types. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load rate plans for selected room type
   */
  const loadRatePlans = async () => {
    try {
      const ratePlansData = await optimizeApi.getRatePlans(selectedRoomType);
      setRatePlans(ratePlansData.rate_plans || []);
      
      // Auto-select first rate plan if available
      if (ratePlansData.rate_plans && ratePlansData.rate_plans.length > 0) {
        setSelectedRatePlan(ratePlansData.rate_plans[0].id);
      } else {
        setSelectedRatePlan('');
      }
    } catch (err) {
      console.error('Error loading rate plans:', err);
      // Don't show error, rate plans are optional
    }
  };

  /**
   * Load pricing recommendations
   */
  const loadRecommendations = async () => {
    if (!selectedRoomType) return;

    try {
      setRefreshing(true);
      setError(null);

      const data = await optimizeApi.getRecommendations(
        selectedRoomType,
        selectedRatePlan,
        days
      );

      setRecommendations(data.recommendations || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Error loading recommendations:', err);
      setError('Failed to load pricing recommendations. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Handle apply single recommendation
   */
  const handleApplyRecommendation = async (recommendation) => {
    try {
      await optimizeApi.applyRecommendation(
        recommendation.date,
        selectedRoomType,
        selectedRatePlan,
        recommendation.recommended_price
      );

      // Reload recommendations
      await loadRecommendations();

      // Show success message (you can add a toast notification here)
      console.log('Recommendation applied successfully');
    } catch (err) {
      console.error('Error applying recommendation:', err);
      alert('Failed to apply recommendation. Please try again.');
    }
  };

  /**
   * Handle apply bulk recommendations
   */
  const handleApplyBulk = async (action, dates = null) => {
    try {
      const result = await optimizeApi.applyBulk(
        selectedRoomType,
        selectedRatePlan,
        action,
        dates
      );

      // Reload recommendations
      await loadRecommendations();

      // Show success message
      alert(`Successfully updated ${result.updated_count} prices`);
    } catch (err) {
      console.error('Error applying bulk:', err);
      alert('Failed to apply bulk recommendations. Please try again.');
    }
  };

  /**
   * Handle export to CSV
   */
  const handleExport = () => {
    const roomTypeName = roomTypes.find(rt => rt.id === selectedRoomType)?.name || 'Unknown';
    optimizeApi.exportToCSV(recommendations, roomTypeName);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error state
  if (error && !selectedRoomType) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Optimize</h1>
          <p className="text-muted-foreground">
            ML-powered pricing recommendations
          </p>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Optimize</h1>
          <p className="text-muted-foreground">
            ML-powered pricing recommendations for the next {days} days
          </p>
        </div>
      </div>

      {/* Filters */}
      <OptimizeFilters
        roomTypes={roomTypes}
        ratePlans={ratePlans}
        selectedRoomType={selectedRoomType}
        selectedRatePlan={selectedRatePlan}
        days={days}
        onRoomTypeChange={setSelectedRoomType}
        onRatePlanChange={setSelectedRatePlan}
        onDaysChange={setDays}
        onRefresh={loadRecommendations}
        onExport={handleExport}
        loading={refreshing}
      />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      {refreshing && !recommendations.length ? (
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">
              Generating pricing recommendations...
            </p>
            <p className="text-sm text-muted-foreground">
              This may take a few moments as we train the ML models
            </p>
          </div>
        </Card>
      ) : recommendations.length > 0 ? (
        <>
          {/* Summary */}
          {summary && (
            <OptimizeSummary
              summary={summary}
              onApplyAll={() => handleApplyBulk('apply_all')}
              loading={refreshing}
            />
          )}

          {/* Pricing Table */}
          <PricingTable
            recommendations={recommendations}
            onApply={handleApplyRecommendation}
            loading={refreshing}
          />
        </>
      ) : (
        <Card className="p-12">
          <div className="text-center text-muted-foreground">
            <p>No recommendations available.</p>
            <p className="text-sm mt-2">
              Select a room type to view pricing recommendations.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Optimize;
